/**
 * AddRecipientsDialog — one dialog for adding addresses to a client, however many.
 *
 * A handover arrives as twelve addresses in a mail. Twelve trips through a single-address
 * dialog is twelve chances to paste one lender's desk onto another lender's client, which
 * is the accident the right-party tick exists to stop and which no server-side check
 * catches: nothing in the request body says which lender the operator believed they were
 * configuring.
 *
 * So this takes the whole paste, and answers three questions before the operator commits:
 *
 *  - **what will be created** — parsed with the algorithm pinned in CONTRACT-RECIPIENTS §2
 *    and shown back, because a parse nobody can see is a parse nobody can check;
 *  - **what is already configured** — folded case-insensitively, reported as a duplicate
 *    rather than an error, and never written twice;
 *  - **what is not an address** — named by the text that was pasted, with the submit
 *    blocked. The server refuses the whole list for the same reason: half a list written
 *    is worse than none, because nobody can tell which half, and the operator re-pastes
 *    the lot while the one mistyped address is the one that now looks fine.
 *
 * The right-party confirmation names every distinct DOMAIN in the paste and is invalidated
 * when that set changes — the same rule the single-address form applies to an edited
 * address, for the same reason. It is still a speed bump and not a control: nothing about
 * it reaches the API, and `_reject_unknown` would refuse it if it did. The real control is
 * that this route is client-scoped.
 *
 * This dialog is mounted from two places (the client's Recipients card and the schedule
 * editor's inline add) so that there is exactly one implementation of that tick. A second
 * add path that re-implemented it is how the guard quietly stops applying.
 */

import { useEffect, useMemo, useState } from 'react';

import Button from '@/components/Button';
import { Checkbox, Field, Input, Select, Textarea } from '@/components/Field';
import Modal from '@/components/Modal';
import { useToast } from '@/components/Toast';
import {
  MAX_BULK_ADDRESSES,
  domainKey,
  joinDomains,
  planAddresses,
  type AddressPlan,
  type ExistingAddress,
} from '@/lib/addresses';
import { api, errorMessage, isApiError } from '@/lib/api';
import { pluralise } from '@/lib/format';
import { RECIPIENT_KINDS } from '@/lib/types';
import type { Recipient, RecipientBulkInvalid, RecipientKind } from '@/lib/types';

const KIND_LABEL: Record<RecipientKind, string> = { to: 'To', cc: 'Cc', bcc: 'Bcc' };

const KIND_HINT: Record<RecipientKind, string> = {
  to: 'Named on the message. At least one active To is required or nothing is delivered.',
  cc: 'Copied, and visible to everyone else on the message.',
  bcc: 'Copied without the other recipients seeing the address.',
};

/** Addresses shown in the read-back before it asks. A paste of 100 is not a read-back. */
const CREATE_PREVIEW = 12;

const DUPLICATE_WORDING: Record<string, string> = {
  already_configured: 'already configured',
  repeated_in_input: 'repeated in this paste',
};

export interface AddRecipientsDialogProps {
  open: boolean;
  clientId: string;
  /** The end lender whose borrower data these addresses will receive. Named in the tick. */
  companyName?: string;
  /** Display name of the client, for copy that has to tell two prefixes apart. */
  clientName?: string;
  /** What the client already has, so a duplicate is named before the round trip. */
  existing?: ReadonlyArray<ExistingAddress>;
  onClose: () => void;
  /** Fired with the rows the server actually created — never with what was parsed. */
  onCreated: (created: Recipient[]) => void;
}

export function AddRecipientsDialog({
  open,
  clientId,
  companyName,
  clientName,
  existing = [],
  onClose,
  onCreated,
}: AddRecipientsDialogProps) {
  const toast = useToast();

  const [input, setInput] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<RecipientKind>('to');
  const [active, setActive] = useState(true);
  /** The domain set the operator ticked for. A new domain in the paste re-asks. */
  const [ackDomains, setAckDomains] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  /** Malformed entries as the SERVER found them — shown when its parse and ours disagree. */
  const [serverInvalid, setServerInvalid] = useState<RecipientBulkInvalid[]>([]);
  /** The read-back lists every address; past this many it asks first. */
  const [showAllCreate, setShowAllCreate] = useState(false);

  const lender = companyName || clientName || clientId;

  useEffect(() => {
    if (!open) return;
    setInput('');
    setName('');
    setKind('to');
    setActive(true);
    setAckDomains(null);
    setFormError(null);
    setServerInvalid([]);
    setShowAllCreate(false);
    setSaving(false);
  }, [open]);

  const plan: AddressPlan = useMemo(() => planAddresses(input, existing), [input, existing]);

  const domains = plan.domains;
  const acknowledged = ackDomains !== null && ackDomains === domainKey(domains);

  /**
   * A single address keeps the single-create route, so its 409 `recipient_exists` still
   * fires when this dialog's copy of the client's rows is stale. One address that already
   * exists is a mistake worth stopping; the same address inside a list of twelve is just
   * the list being complete, which is why the bulk route reports it instead.
   */
  const single = plan.entries.length === 1 && plan.create.length === 1;
  const onlyEntryHasName = single && plan.create[0]!.name.length > 0;

  /**
   * Why the submit is disabled, and whether a callout below already says so. Two of the
   * five cases had no callout, so their only statement was `title` on a disabled button
   * — invisible on keyboard, on touch, in a screenshot, and below the fold.
   */
  const blocker: { message: string; covered: boolean } | null =
    plan.entries.length === 0
      ? { message: 'Paste at least one address.', covered: false }
      : plan.tooLarge
        ? { message: `At most ${MAX_BULK_ADDRESSES} addresses at a time.`, covered: true }
        : plan.invalid.length > 0
          ? {
              message:
                plan.invalid.length === 1
                  ? 'One entry is not an address.'
                  : `${plan.invalid.length} entries are not addresses.`,
              covered: true,
            }
          : plan.create.length === 0
            ? {
                message: 'Every address here is already configured for this client.',
                covered: false,
              }
            : !acknowledged
              ? {
                  message: `Confirm that these addresses should receive ${lender} borrower data.`,
                  covered: true,
                }
              : null;
  const blocking = blocker?.message ?? null;

  const submit = async (): Promise<void> => {
    if (blocking || saving) return;
    setSaving(true);
    setFormError(null);
    setServerInvalid([]);
    try {
      if (single) {
        const entry = plan.create[0]!;
        const created = await api.recipients.create(clientId, {
          email: entry.email,
          name: entry.name || name.trim(),
          kind,
          active,
        });
        toast.success(`${entry.email} added to ${clientId}.`, {
          description: `They will receive ${lender} borrower data on every dispatch for this client.`,
        });
        onCreated([created]);
      } else {
        // The raw paste goes to the server, not our parsed list: the API parses it again
        // with the same pinned algorithm, and sending our interpretation instead would
        // make a drift between the two parsers invisible rather than loud.
        const result = await api.recipients.createBulk(clientId, { input, kind, active });
        const duplicates = result.duplicate_count ?? result.duplicates.length;
        toast.success(
          `${pluralise(result.created_count ?? result.created.length, 'address', 'addresses')} added to ${clientId}.`,
          {
            description: duplicates
              ? `${duplicates} already configured, so not added again. Everyone added receives ${lender} borrower data.`
              : `They will receive ${lender} borrower data on every dispatch for this client.`,
          },
        );
        onCreated(result.created);
      }
      onClose();
    } catch (err) {
      setFormError(errorMessage(err));
      // The server validates the whole list before writing anything, so its refusal is
      // the authoritative list of what is wrong — show it verbatim rather than ours.
      if (isApiError(err) && err.details && typeof err.details === 'object') {
        const detail = err.details as { invalid?: RecipientBulkInvalid[] };
        if (Array.isArray(detail.invalid)) setServerInvalid(detail.invalid);
      }
      if (!isApiError(err)) toast.error('Could not add the addresses.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible={!saving}
      hideClose={saving}
      // A read-back of a dozen desks does not fit 520px, and the whole point of it is
      // that every address can be read.
      size={plan.entries.length > 4 ? 'lg' : 'md'}
      title="Add recipients"
      description={`Every address added here receives ${lender} borrower data on every dispatch for ${clientId}.`}
      footer={
        <>
          {blocker && !blocker.covered ? (
            <span className="small" style={{ color: 'var(--danger)' }}>
              {blocker.message}
            </span>
          ) : null}
          <span className="grow" />
          <Button variant="ghost" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={saving}
            disabled={Boolean(blocking)}
            title={blocking ?? undefined}
            onClick={() => void submit()}
          >
            {plan.create.length > 1
              ? `Add ${plan.create.length} recipients`
              : 'Add recipient'}
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field
          label="Email addresses"
          required
          hint={
            <>
              One or many. Separate with commas, semicolons, spaces or new lines;{' '}
              <span className="mono">Collections Desk &lt;coll@example.com&gt;</span> keeps the
              name.
            </>
          }
        >
          {(control) => (
            <Textarea
              {...control}
              mono
              rows={4}
              spellCheck={false}
              autoComplete="off"
              value={input}
              invalid={plan.invalid.length > 0 || plan.tooLarge}
              placeholder="ops@example.com, risk@example.com"
              onChange={(event) => {
                setInput(event.target.value);
                setFormError(null);
                setServerInvalid([]);
              }}
            />
          )}
        </Field>

        {/* ------------------------------------------------------------ right party */}
        <div className={`callout tone-${acknowledged ? 'info' : 'warn'}`}>
          <span className="callout-glyph" aria-hidden="true">
            {acknowledged ? 'i' : '▲'}
          </span>
          <Checkbox
            checked={acknowledged}
            disabled={domains.length === 0 || saving}
            onChange={(event) => {
              setAckDomains(event.target.checked ? domainKey(domains) : null);
              setFormError(null);
            }}
            label={
              domains.length === 0 ? (
                <>These addresses should receive <strong>{lender}</strong> borrower data</>
              ) : (
                <>
                  Addresses on <strong>{joinDomains(domains)}</strong> should receive{' '}
                  <strong>{lender}</strong> borrower data
                </>
              )
            }
            hint={`Client ${clientId}${clientName ? ` — ${clientName}` : ''}. ${
              domains.length > 1
                ? `This paste spans ${domains.length} domains. `
                : ''
            }Sending one lender's report to another lender's desk cannot be undone by revoking the link.`}
          />
        </div>

        {/* A name typed into a box can only belong to one address, so it is offered only
            when the paste holds exactly one and that one carries no display name. */}
        {single && !onlyEntryHasName ? (
          <Field label="Name" optional hint="Shown in the greeting; not used for routing.">
            {(control) => (
              <Input
                {...control}
                value={name}
                disabled={saving}
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>
        ) : plan.create.length > 1 ? (
          <div className="xs dim">
            Names come from the paste. Anything in{' '}
            <span className="mono">Name &lt;addr&gt;</span> form is kept; the rest are stored
            without one.
          </div>
        ) : null}

        <Field label="Kind" hint={KIND_HINT[kind]}>
          {(control) => (
            <Select
              {...control}
              value={kind}
              disabled={saving}
              onChange={(event) => setKind(event.target.value as RecipientKind)}
            >
              {RECIPIENT_KINDS.map((value) => (
                <option key={value} value={value}>
                  {KIND_LABEL[value]}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Checkbox
          checked={active}
          disabled={saving}
          onChange={(event) => setActive(event.target.checked)}
          label="Active"
          hint={
            plan.create.length > 1
              ? 'Applies to every address in this paste. Inactive recipients stay configured but receive nothing.'
              : 'Inactive recipients stay configured but receive nothing.'
          }
        />

        {/* ------------------------------------------------------------ readback */}
        {plan.entries.length > 0 ? (
          <div className="callout tone-info">
            <span className="callout-glyph" aria-hidden="true">
              ≡
            </span>
            <div className="stack-tight" style={{ minWidth: 0 }}>
              <span>
                <strong>{pluralise(plan.entries.length, 'address', 'addresses')}</strong> found ·{' '}
                {plan.create.length} to add
                {plan.duplicates.length > 0
                  ? ` · ${plan.duplicates.length} already configured or repeated`
                  : ''}
                {plan.invalid.length > 0 ? ` · ${plan.invalid.length} not an address` : ''}
              </span>
              {/* One address per line, at full body width, wrapping rather than
                  truncating. The two-column 230px grid cut every address at ~34
                  characters — so two desks at one long domain read as the same string,
                  which is exactly the accident this read-back exists to stop. */}
              {plan.create.length > 0 ? (
                <div className="stack-tight" style={{ minWidth: 0 }}>
                  {(showAllCreate ? plan.create : plan.create.slice(0, CREATE_PREVIEW)).map(
                    (entry) => (
                      <span key={entry.email} className="mono xs" style={{ overflowWrap: 'anywhere' }}>
                        {entry.email}
                        {entry.name ? <span className="dim"> · {entry.name}</span> : null}
                      </span>
                    ),
                  )}
                  {!showAllCreate && plan.create.length > CREATE_PREVIEW ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowAllCreate(true)}
                      style={{ alignSelf: 'flex-start' }}
                    >
                      +{plan.create.length - CREATE_PREVIEW} more
                    </Button>
                  ) : null}
                </div>
              ) : null}
              {plan.duplicates.length > 0 ? (
                <div className="stack-tight" style={{ minWidth: 0 }}>
                  {plan.duplicates.map((entry) => (
                    <span
                      key={`${entry.index}-${entry.email}`}
                      className="xs dim"
                      style={{ overflowWrap: 'anywhere' }}
                    >
                      <span className="mono">{entry.email}</span> —{' '}
                      {DUPLICATE_WORDING[entry.reason] ?? entry.reason}, not added again
                    </span>
                  ))}
                </div>
              ) : null}
              {domains.length > 0 ? (
                <span className="xs dim">
                  {pluralise(domains.length, 'domain')}: {joinDomains(domains)}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}

        {plan.tooLarge ? (
          <div className="callout tone-danger" role="alert">
            <span className="callout-glyph" aria-hidden="true">
              ✕
            </span>
            <span>
              That is more than {MAX_BULK_ADDRESSES} addresses. This list holds tens of desks per
              client, so a paste this size is a mistake — and a mistake that writes thousands of
              rows is not one anybody unpicks by hand.
            </span>
          </div>
        ) : null}

        {plan.invalid.length > 0 ? (
          <div className="callout tone-danger" role="alert">
            <span className="callout-glyph" aria-hidden="true">
              ✕
            </span>
            <div className="stack-tight" style={{ minWidth: 0 }}>
              <span>
                <strong>
                  {plan.invalid.length === 1
                    ? 'One entry cannot be read as an address.'
                    : `${plan.invalid.length} entries cannot be read as addresses.`}
                </strong>{' '}
                Nothing will be written until{' '}
                {plan.invalid.length === 1 ? 'it is' : 'they are'} fixed or removed — a
                half-written list cannot be told apart from a complete one.
              </span>
              {/* The submit is blocked until this is fixed, and the text needed to fix
                  it was the text being ellipsised. */}
              {plan.invalid.map((entry) => (
                <span
                  key={`${entry.index}-${entry.input}`}
                  className="xs"
                  style={{ overflowWrap: 'anywhere' }}
                >
                  <span className="dim">#{entry.index + 1}</span>{' '}
                  <span className="mono">{entry.input}</span>
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {formError ? (
          <div className="callout tone-danger" role="alert">
            <span className="callout-glyph" aria-hidden="true">
              ✕
            </span>
            <div className="stack-tight" style={{ minWidth: 0 }}>
              <span>{formError}</span>
              {/* Including the server's `reason`, which is an error and was being cut. */}
              {serverInvalid.map((entry) => (
                <div key={`${entry.index}-${entry.input}`} className="stack-tight">
                  <span className="xs" style={{ overflowWrap: 'anywhere' }}>
                    <span className="dim">#{entry.index + 1}</span>{' '}
                    <span className="mono">{entry.input}</span>
                  </span>
                  {entry.reason ? (
                    <span className="xs dim" style={{ overflowWrap: 'anywhere' }}>
                      {entry.reason}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

export default AddRecipientsDialog;
